import React from 'react';
import {
  CheckCircle, Clock, AlertTriangle, XCircle, AlertCircle, FileText, Loader, Inbox, Eye,
} from 'react-feather';

/**
 * The single run-state vocabulary for ETL Configuration.
 *
 * Three tables used to describe the same pipeline in three different
 * palettes — FileTrackingShared.STATUS_CONFIG (tracking view),
 * ConfigurationsSection.OUTCOME_STYLE (etlFileProcessingState) and the ad-hoc
 * badge colours in DlqSection — and they disagreed: `resolved` was grey in
 * one and green in another, `dlq` had no entry in the third. One map now
 * covers the union of both state machines.
 *
 * tone drives colour only: ok · warn · error · info · busy · muted.
 */
export const RUN_STATES = {
  // ── etlFileProcessingState (the DLQ state machine) ──────────────
  normalized:        { label: 'Processed',    tone: 'ok',    Icon: CheckCircle },
  ocrComplete:       { label: 'OCR complete', tone: 'busy',  Icon: Loader },
  ocrPending:        { label: 'OCR pending',  tone: 'busy',  Icon: Loader },
  normalizerPending: { label: 'Normalizing',  tone: 'busy',  Icon: Loader },
  dlq:               { label: 'Dead letter',  tone: 'error', Icon: AlertCircle },

  // ── file tracking (did the file we expect turn up?) ─────────────
  processed:    { label: 'Processed',     tone: 'ok',    Icon: CheckCircle },
  received:     { label: 'Received',      tone: 'info',  Icon: Inbox },
  processing:   { label: 'Processing',    tone: 'busy',  Icon: Loader },
  pending:      { label: 'Not yet received', tone: 'warn', Icon: Clock },
  missed:       { label: 'Expected — not received', tone: 'error', Icon: AlertTriangle },
  needs_review: { label: 'Needs review',  tone: 'info',  Icon: Eye },

  // ── shared terminals ────────────────────────────────────────────
  failed:   { label: 'Failed',   tone: 'error', Icon: XCircle },
  resolved: { label: 'Resolved', tone: 'ok',    Icon: CheckCircle },
  skipped:  { label: 'Skipped',  tone: 'muted', Icon: FileText },
  never:    { label: 'Never run', tone: 'muted', Icon: Clock },
};

/** Look a state up without having to guard for unknown strings. */
export function runState(status) {
  if (!status) return RUN_STATES.never;
  return RUN_STATES[status] || { label: String(status), tone: 'muted', Icon: FileText };
}

/** True when the pipeline is still moving — callers use this to decide whether to poll. */
export function isInFlight(status) {
  return runState(status).tone === 'busy';
}

/**
 * RunStatePill — the only status pill on these pages.
 *
 * props
 *   status   a RUN_STATES key (unknown strings render as a muted literal)
 *   label    override the mapped label
 *   title    tooltip, e.g. the failure message
 *   icon     show the state icon as well as the dot (default false — the dot
 *            alone is enough in a dense table)
 *   large    bigger pill for detail panes
 */
export default function RunStatePill({ status, label, title, icon = false, large = false }) {
  const s = runState(status);
  const Icon = s.Icon;
  return (
    <span
      className={`ep-pill ep-tone-${s.tone}${large ? ' ep-lg' : ''}`}
      title={title || undefined}
    >
      {icon ? <Icon size={12} /> : <span className="ep-pill-dot" aria-hidden="true" />}
      {label || s.label}
    </span>
  );
}
